import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  Check,
  X,
  TrendingUp,
  Target,
  Database,
  Layers,
  AlertTriangle,
  Sparkles,
  Gauge,
} from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlacarTab } from "@/components/admin/marketing/PlacarTab";
import { PerformanceTab } from "@/components/admin/marketing/PerformanceTab";
import {
  getMarketingIntel,
  updateMktMonthly,
  type MktMonthly,
} from "@/lib/marketing/intel.functions";
import { getMktDaily } from "@/lib/marketing/daily.functions";
import { insightsAutomaticos, placarPorMes, placarTotais } from "@/lib/marketing/analise";
import {
  brl,
  CATEGORIAS,
  CATEGORIA_COLORS,
  dec,
  int,
  MES_LABEL,
} from "@/lib/marketing/format";

export const Route = createFileRoute("/admin/inteligencia-marketing")({
  head: () => ({
    meta: [
      { title: "Inteligência de Marketing — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/inteligencia-marketing">
      <IntelPage />
    </AdminGate>
  ),
});

const NUM_FIELDS: { key: keyof MktMonthly; label: string; money?: boolean }[] = [
  { key: "faturamento", label: "Faturamento", money: true },
  { key: "meta", label: "Meta", money: true },
  { key: "investido", label: "Investido", money: true },
  { key: "roas", label: "ROAS" },
  { key: "faturas_pagas", label: "Faturas pagas" },
  { key: "vendas_star", label: "Vendas Star" },
  { key: "vendas_certificacoes", label: "Vendas Cert." },
  { key: "leads_captados", label: "Leads" },
];

const INSIGHT_STYLE: Record<string, string> = {
  destaque: "border-emerald-400/30 bg-emerald-400/[0.07]",
  alerta: "border-amber-400/30 bg-amber-400/[0.07]",
  cobertura: "border-white/10 bg-white/[0.03]",
};

function IntelPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mkt-intel"],
    queryFn: () => getMarketingIntel(),
  });
  const daily = useQuery({
    queryKey: ["mkt-daily"],
    queryFn: () => getMktDaily(),
    staleTime: 300_000,
  });

  const actions = data?.actions ?? [];
  const monthly = data?.monthly ?? [];
  const insights = data?.insights ?? [];
  const pages = data?.pages ?? [];
  const dailyRows = daily.data ?? [];

  const mesesPlacar = useMemo(() => placarPorMes(dailyRows), [dailyRows]);
  const placar = useMemo(() => placarTotais(mesesPlacar), [mesesPlacar]);
  const autoInsights = useMemo(
    () => insightsAutomaticos(actions, mesesPlacar, monthly),
    [actions, mesesPlacar, monthly],
  );

  const kpis = useMemo(() => {
    const completo = actions.filter((a) => a.completude === "completo").length;
    const parcial = actions.filter((a) => a.completude === "parcial").length;
    const semDado = actions.filter((a) => a.completude === "sem_dado").length;
    const receita = actions.reduce((s, a) => s + (a.receita ?? 0), 0);
    const investido = actions.reduce((s, a) => s + (a.investido ?? 0), 0);
    return { total: actions.length, completo, parcial, semDado, receita, investido };
  }, [actions]);

  const chartData = monthly.map((m) => ({
    mes: MES_LABEL(m.mes),
    Realizado: m.faturamento ?? 0,
    Meta: m.meta ?? 0,
    ROAS: m.roas ?? 0,
  }));

  const porCategoria = CATEGORIAS.map((c) => ({
    categoria: c,
    total: actions.filter((a) => a.categoria === c).length,
    receita: actions
      .filter((a) => a.categoria === c)
      .reduce((s, a) => s + (a.receita ?? 0), 0),
  })).filter((c) => c.total > 0);

  return (
    <div className="min-h-screen px-4 py-10">
      <div className="mx-auto max-w-7xl">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 mb-2"
        >
          <ArrowLeft className="h-3 w-3" /> Painel de Controle
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Inteligência de Marketing
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Histórico real da operação: placar diário do time, metas mensais e todas as ações de
              venda desde 2022. Campo sem fonte fica vazio — nada é estimado.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
              <Link to="/admin/acoes-marketing">Ver ações detalhadas</Link>
            </Button>
            <Button asChild variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white">
              <Link to="/admin/inteligencia-leads">Inteligência Leads/CRM</Link>
            </Button>
          </div>
        </div>

        {isLoading && <div className="text-white/50 text-sm py-10 text-center">Carregando…</div>}

        {data && (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
              <KpiCard
                icon={<Gauge className="h-4 w-4" />}
                label="ROAS real do placar"
                value={placar.roas === null ? "—" : dec(placar.roas, "x")}
                hint={
                  placar.de && placar.ate
                    ? `${MES_LABEL(placar.de)} → ${MES_LABEL(placar.ate)} · ${brl(placar.faturamento)} / ${brl(placar.gasto)}`
                    : "sem placar importado"
                }
              />
              <KpiCard
                icon={<TrendingUp className="h-4 w-4" />}
                label="Faturamento no placar"
                value={brl(placar.faturamento)}
                hint={`${int(placar.faturas_pagas)} faturas pagas · ticket ${brl(placar.ticket)}`}
              />
              <KpiCard
                icon={<Target className="h-4 w-4" />}
                label="Mídia paga no placar"
                value={brl(placar.gasto)}
                hint={placar.canais.map((c) => `${c.canal} ${dec(c.pct, "%")}`).join(" · ")}
              />
              <KpiCard
                icon={<Layers className="h-4 w-4" />}
                label="Custo por venda"
                value={brl(placar.cac)}
                hint="mídia paga ÷ faturas pagas do placar"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-8">
              <KpiCard icon={<Layers className="h-4 w-4" />} label="Ações mapeadas" value={int(kpis.total)} hint={`${kpis.completo} completas · ${kpis.parcial} parciais · ${kpis.semDado} sem dado`} />
              <KpiCard
                icon={<Database className="h-4 w-4" />}
                label="Receita somada das ações"
                value={brl(kpis.receita)}
                hint="soma apenas das ações com receita registrada"
              />
              <KpiCard
                icon={<Target className="h-4 w-4" />}
                label="Investido somado nas ações"
                value={brl(kpis.investido)}
                hint="soma apenas das ações com investimento registrado"
              />
            </div>

            <Tabs defaultValue="geral">
              <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
                <TabsTrigger value="geral">Visão geral</TabsTrigger>
                <TabsTrigger value="placar">Placar diário</TabsTrigger>
                <TabsTrigger value="performance">Performance das ações</TabsTrigger>
                <TabsTrigger value="mensal">Histórico mensal</TabsTrigger>
                <TabsTrigger value="insights">Insights</TabsTrigger>
                <TabsTrigger value="paginas">Páginas / LPs</TabsTrigger>
              </TabsList>

              <TabsContent value="geral" className="mt-6 space-y-6">
                <Panel title="Faturamento realizado vs meta (mês a mês)">
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="mes" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                        <YAxis
                          stroke="rgba(255,255,255,0.4)"
                          fontSize={11}
                          tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
                        />
                        <RTooltip
                          contentStyle={{ background: "#13123a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                          formatter={(v: number) => brl(v)}
                        />
                        <Bar dataKey="Realizado" fill="#f5b022" radius={[4, 4, 0, 0]} />
                        <Line dataKey="Meta" stroke="#7dd3fc" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                <Panel title="Leitura automática dos dados">
                  <div className="grid gap-2 md:grid-cols-2">
                    {autoInsights.map((i, idx) => (
                      <div
                        key={idx}
                        className={`rounded-xl border p-4 text-xs leading-relaxed text-white/80 ${INSIGHT_STYLE[i.tipo]}`}
                      >
                        <span className="mb-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/45">
                          {i.tipo === "alerta" ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : (
                            <Sparkles className="h-3 w-3" />
                          )}
                          {i.tipo}
                        </span>
                        <p>{i.texto}</p>
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel title="Ações por categoria">
                  <div className="space-y-2">
                    {porCategoria.map((c) => (
                      <div key={c.categoria} className="flex items-center justify-between gap-3 text-sm">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] ${CATEGORIA_COLORS[c.categoria] ?? ""}`}
                        >
                          {c.categoria}
                        </span>
                        <span className="text-white/50 text-xs shrink-0">
                          {c.total} {c.total === 1 ? "ação" : "ações"} · {brl(c.receita)}
                        </span>
                      </div>
                    ))}
                  </div>
                </Panel>
              </TabsContent>

              <TabsContent value="placar" className="mt-6">
                {daily.isLoading ? (
                  <p className="py-10 text-center text-sm text-white/50">Carregando placar…</p>
                ) : (
                  <PlacarTab rows={dailyRows} />
                )}
              </TabsContent>

              <TabsContent value="performance" className="mt-6">
                <PerformanceTab actions={actions} />
              </TabsContent>

              <TabsContent value="mensal" className="mt-6 space-y-6">
                <Panel title="ROAS por mês">
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="mes" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                        <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                        <RTooltip
                          contentStyle={{ background: "#13123a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                        />
                        <Line dataKey="ROAS" stroke="#34d399" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
                <MonthlyTable rows={monthly} onSaved={() => qc.invalidateQueries({ queryKey: ["mkt-intel"] })} />
              </TabsContent>

              <TabsContent value="insights" className="mt-6 space-y-3">
                {autoInsights.map((i, idx) => (
                  <div
                    key={`auto-${idx}`}
                    className={`rounded-2xl border p-5 ${INSIGHT_STYLE[i.tipo]}`}
                  >
                    <span className="text-[10px] uppercase tracking-wider text-white/45">
                      automático · {i.tipo}
                    </span>
                    <p className="mt-2 text-sm leading-relaxed text-white/80">{i.texto}</p>
                  </div>
                ))}
                {insights.map((i) => (
                  <div key={i.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                    <span className="text-[10px] uppercase tracking-wider text-admin-accent">{i.tipo}</span>
                    <p className="mt-2 text-sm text-white/80 leading-relaxed">{i.texto}</p>
                    {i.acoes_relacionadas && (
                      <p className="mt-3 text-[11px] text-white/40">
                        Ações relacionadas: {i.acoes_relacionadas}
                      </p>
                    )}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="paginas" className="mt-6 space-y-3">
                {pages.map((p) => (
                  <div key={p.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-white font-semibold">{p.acao}</h3>
                      <span className="text-[11px] text-white/40">{p.canal ?? "—"}</span>
                    </div>
                    <p className="mt-2 text-xs text-white/60 break-words">{p.urls}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}


function KpiCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <div className="flex items-center gap-2 text-white/50 text-[11px] uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-xl font-bold text-white">{value}</div>
      {hint && <div className="mt-1 text-[10px] text-white/35">{hint}</div>}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <h2 className="text-sm font-semibold text-white/80 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function MonthlyTable({ rows, onSaved }: { rows: MktMonthly[]; onSaved: () => void }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const mut = useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, number | null> }) =>
      updateMktMonthly({ data: vars }),
    onSuccess: () => {
      toast.success("Mês atualizado");
      setEditing(null);
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function startEdit(row: MktMonthly) {
    setEditing(row.id);
    const d: Record<string, string> = {};
    for (const f of NUM_FIELDS) {
      const v = row[f.key];
      d[f.key as string] = v === null || v === undefined ? "" : String(v);
    }
    setDraft(d);
  }

  function save(id: string) {
    const patch: Record<string, number | null> = {};
    for (const f of NUM_FIELDS) {
      const raw = (draft[f.key as string] ?? "").trim().replace(",", ".");
      patch[f.key as string] = raw === "" ? null : Number(raw);
      if (raw !== "" && Number.isNaN(Number(raw))) {
        toast.error(`Valor inválido em ${f.label}`);
        return;
      }
    }
    mut.mutate({ id, patch });
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
      <div className="p-5 pb-3">
        <h2 className="text-sm font-semibold text-white/80">Histórico mensal (editável)</h2>
        <p className="text-[11px] text-white/40 mt-1">
          % da meta é calculado automaticamente a partir dos valores informados.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-white/40 border-y border-white/10">
              <th className="px-4 py-2 text-left">Mês</th>
              <th className="px-3 py-2 text-right">Faturamento</th>
              <th className="px-3 py-2 text-right">Meta</th>
              <th className="px-3 py-2 text-right">% Meta</th>
              <th className="px-3 py-2 text-right">Investido</th>
              <th className="px-3 py-2 text-right">Saldo</th>
              <th className="px-3 py-2 text-right">ROAS</th>
              <th className="px-3 py-2 text-right">Pagas</th>
              <th className="px-3 py-2 text-right">Star</th>
              <th className="px-3 py-2 text-right">Cert.</th>
              <th className="px-3 py-2 text-right">Leads</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isEdit = editing === r.id;
              const pctMeta =
                r.faturamento !== null && r.meta !== null && r.meta !== 0
                  ? (r.faturamento / r.meta) * 100
                  : null;
              const saldo =
                r.faturamento !== null && r.investido !== null ? r.faturamento - r.investido : null;
              return (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="px-4 py-2 text-white/80 whitespace-nowrap">{MES_LABEL(r.mes)}</td>
                  {isEdit ? (
                    <>
                      {NUM_FIELDS.filter((f) => f.key === "faturamento" || f.key === "meta").map((f) => (
                        <td key={f.key as string} className="px-2 py-1">
                          <Cell
                            value={draft[f.key as string] ?? ""}
                            onChange={(v) => setDraft((d) => ({ ...d, [f.key as string]: v }))}
                          />
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right text-white/30">—</td>
                      <td className="px-2 py-1">
                        <Cell
                          value={draft["investido"] ?? ""}
                          onChange={(v) => setDraft((d) => ({ ...d, investido: v }))}
                        />
                      </td>
                      <td className="px-3 py-2 text-right text-white/30">—</td>
                      {(["roas", "faturas_pagas", "vendas_star", "vendas_certificacoes", "leads_captados"] as const).map(
                        (k) => (
                          <td key={k} className="px-2 py-1">
                            <Cell
                              value={draft[k] ?? ""}
                              onChange={(v) => setDraft((d) => ({ ...d, [k]: v }))}
                            />
                          </td>
                        ),
                      )}
                      <td className="px-2 py-1 whitespace-nowrap">
                        <button
                          onClick={() => save(r.id)}
                          className="text-emerald-300 hover:text-emerald-200 p-1"
                          aria-label="Salvar"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="text-white/40 hover:text-white p-1"
                          aria-label="Cancelar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-right text-white/80">{brl(r.faturamento)}</td>
                      <td className="px-3 py-2 text-right text-white/60">{brl(r.meta)}</td>
                      <td className="px-3 py-2 text-right text-white/60">
                        {pctMeta === null ? "—" : dec(pctMeta, "%")}
                      </td>
                      <td className="px-3 py-2 text-right text-white/60">{brl(r.investido)}</td>
                      <td className="px-3 py-2 text-right text-white/60">{brl(saldo)}</td>
                      <td className="px-3 py-2 text-right text-white/80">{dec(r.roas, "x")}</td>
                      <td className="px-3 py-2 text-right text-white/60">{int(r.faturas_pagas)}</td>
                      <td className="px-3 py-2 text-right text-white/60">{int(r.vendas_star)}</td>
                      <td className="px-3 py-2 text-right text-white/60">{int(r.vendas_certificacoes)}</td>
                      <td className="px-3 py-2 text-right text-white/60">{int(r.leads_captados)}</td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => startEdit(r)}
                          className="text-white/40 hover:text-white p-1"
                          aria-label="Editar mês"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cell({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputMode="decimal"
      className="h-8 w-24 bg-white/5 border-white/10 text-right text-xs"
    />
  );
}
